import { useNotification } from 'admin';
import { EvidenceUploadMode } from 'api';
import { useState } from 'react';
import {
  Box,
  IconButton,
  MediaDialog,
  type PhotoEvidenceItem,
  PhotoEvidenceList,
  Stack,
  Tooltip,
  Typography,
  useBreakpoint,
} from 'ui';
import { Delete, Sync } from 'ui/icons';

import { useOfflineEvidence } from '../../hooks/useOfflineEvidence';

export const OfflineEvidenceGallery = ({
  isOnline,
  parentId,
  mode,
  onSyncComplete,
}: {
  isOnline: boolean;
  parentId: string;
  mode: EvidenceUploadMode;
  onSyncComplete?: () => void;
}) => {
  const { isMobile } = useBreakpoint();
  const { open } = useNotification();
  const { offlinePhotos, clearOfflineEvidence, syncOfflineEvidence, isSyncing, isClearing } = useOfflineEvidence({
    isOnline,
    mode,
    onSyncComplete,
    parentId,
  });

  const offlineCarouselMedia = offlinePhotos.map((photo) => photo.url);
  const [selectedOfflineImage, setSelectedOfflineImage] = useState<string>('');

  const handleSync = async () => {
    const result = await syncOfflineEvidence();
    open?.({
      message: result.message,
      type: result.success ? 'success' : 'error',
    });
  };

  const handleClear = async () => {
    try {
      await clearOfflineEvidence();
      open?.({
        message: 'Successfully cleared offline evidence',
        type: 'success',
      });
    } catch (error) {
      open?.({
        message: 'Failed to clear offline evidence',
        type: 'error',
      });
    }
  };

  const getTooltipMessage = () => {
    if (isSyncing) {
      return 'Sync in progress...';
    }
    if (!isOnline) {
      return 'Cannot sync while offline';
    }
    return 'Sync offline evidence';
  };

  return (
    <>
      <Stack display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body1" fontWeight="bold">
          Offline evidence
        </Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Clear offline evidence">
            <div>
              <IconButton onClick={handleClear} disabled={isSyncing || isClearing}>
                <Delete />
              </IconButton>
            </div>
          </Tooltip>
          <Tooltip title={getTooltipMessage()}>
            <div>
              <IconButton disabled={!isOnline || isSyncing || isClearing} onClick={handleSync}>
                <Sync
                  sx={{
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(0deg)',
                      },
                      '100%': {
                        transform: 'rotate(-360deg)',
                      },
                    },
                    animation: isSyncing ? 'spin 2s linear infinite' : 'none',
                  }}
                />
              </IconButton>
            </div>
          </Tooltip>
        </Box>
      </Stack>
      <PhotoEvidenceList
        openOnClick
        slotProps={{
          imageList: {
            cols: isMobile ? 3 : 5,
          },
        }}
        evidencePhotos={offlinePhotos.map((photo) => ({
          src: photo.url,
        }))}
        handleClick={(photo: PhotoEvidenceItem) => setSelectedOfflineImage(photo.src)}
      />

      <MediaDialog
        open={Boolean(selectedOfflineImage)}
        selectedMedium={selectedOfflineImage}
        onClose={() => setSelectedOfflineImage('')}
        media={offlineCarouselMedia}
        handleNavigate={setSelectedOfflineImage}
      />
    </>
  );
};
